import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../lib/api';
import type { Transaction, CreateTransactionRequest, UpdateTransactionRequest, TransactionStats } from '../types/transaction';

interface TransactionFilters {
    account_id?: number;
    type?: string;
    category?: string;
}

export function useTransactions(filters?: TransactionFilters) {
    const queryParams = new URLSearchParams();
    if (filters?.account_id) queryParams.append('account_id', filters.account_id.toString());
    if (filters?.type) queryParams.append('type', filters.type);
    if (filters?.category) queryParams.append('category', filters.category);

    const queryString = queryParams.toString();
    const endpoint = `/api/transactions${queryString ? `?${queryString}` : ''}`;

    return useQuery<Transaction[]>({
        queryKey: ['transactions', filters],
        queryFn: () => apiRequest<Transaction[]>(endpoint),
    });
}

export function useTransaction(id: number) {
    return useQuery<Transaction>({
        queryKey: ['transactions', id],
        queryFn: () => apiRequest<Transaction>(`/api/transactions/${id}`),
        enabled: !!id,
    });
}

export function useCreateTransaction() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateTransactionRequest) =>
            apiRequest<Transaction>('/api/transactions', {
                method: 'POST',
                body: JSON.stringify(data),
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
            queryClient.invalidateQueries({ queryKey: ['accounts'] });
            queryClient.invalidateQueries({ queryKey: ['transaction-stats'] });
        },
    });
}

export function useUpdateTransaction(id: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: UpdateTransactionRequest) =>
            apiRequest<Transaction>(`/api/transactions/${id}`, {
                method: 'PUT',
                body: JSON.stringify(data),
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
            queryClient.invalidateQueries({ queryKey: ['accounts'] });
            queryClient.invalidateQueries({ queryKey: ['transaction-stats'] });
        },
    });
}

export function useDeleteTransaction() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) =>
            apiRequest<void>(`/api/transactions/${id}`, {
                method: 'DELETE',
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
            queryClient.invalidateQueries({ queryKey: ['accounts'] });
            queryClient.invalidateQueries({ queryKey: ['transaction-stats'] });
        },
    });
}

export function useTransactionStats() {
    return useQuery<TransactionStats>({
        queryKey: ['transaction-stats'],
        queryFn: () => apiRequest<TransactionStats>('/api/transactions/stats'),
    });
}
